import {useQueryClient} from '@tanstack/react-query';
import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {FormFieldT} from '@/lib/types/form.types';
import {marketInsertT, marketT} from '@/lib/types/market.types';
import {statsRowT} from '@/lib/types/stats.types';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useMarkets} from '@/providers/components/marketsProvider';

import {ConfirmDeleteModalComponent, ModalFormComponent, StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTableRpc} from '@/components/dataTableRpc';

const initialFormData: marketInsertT = {
  market_name: '',
  market_geo_points: [],
  created_by: '',
  date_created: '',
  date_modified: '',
};

const createFields: FormFieldT<marketInsertT>[] = [
  {
    type: 'text',
    label: 'Name',
    name: 'market_name',
    placeholder: 'Market Name',
    required: true,
  },
  {
    type: 'map',
    label: 'Geo Points',
    name: 'market_geo_points',
    placeholder: 'Geo Points',
    required: true,
  },
];

const editFields: FormFieldT<marketInsertT>[] = [...createFields];

function AdminMarketsPage() {
  const queryClient = useQueryClient();
  const {isAdmin} = useAuth();
  const {deleteData, createData, updateData, getMarket} = useMarkets();

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Refs
  const formDataRef = useRef<marketInsertT>(initialFormData);
  const selectedDataIdRef = useRef<number | null>(null);

  // TanStack queries
  const {data: stats, refetch: refetchStats} = useViewQuery({view: 'market_stats'});

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({queryKey: ['get_market_summary']});
    await queryClient.refetchQueries({queryKey: ['get_market_summary']});
  };

  // Functions
  const handleCreate = async (data: marketT) => {
    try {
      setIsSubmitting(true);
      await createData(data);
      refetchStats();
      setIsVisible(false);
      invalidateQuery();
    } catch (err) {
      setSubmissionError(`Failed to create ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: marketT) => {
    try {
      setIsSubmitting(true);
      await updateData(data);
      refetchStats();
      setIsVisible(false);
      invalidateQuery();
    } catch (err) {
      setSubmissionError(`Failed to update: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedDataIdRef.current === null) {
      setDeleteError('No market selected');
      return;
    }
    try {
      setIsSubmitting(true);
      await deleteData(selectedDataIdRef.current);
      setIsDeleteModalVisible(false);
      invalidateQuery();
    } catch (err) {
      setSubmissionError(`Failed to delete user ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    formDataRef.current = initialFormData;
    setIsEditing(false);
    setIsVisible(true);
  };

  const handleEdit = async (marketId: number) => {
    const market = await getMarket(marketId);
    formDataRef.current = market as marketInsertT;
    setIsEditing(true);
    setIsVisible(true);
  };

  const handleDeleteEvent = async (marketId: number) => {
    selectedDataIdRef.current = marketId;
    setIsDeleteModalVisible(true);
  };

  // Stats
  const statsRow: statsRowT = [
    {
      title: 'Total Markets',
      value: stats?.total,
      subtitle: 'This Month',
      change: 1.03,
      icon: 'ri-price-tag-3-line',
      color: 'primary',
      cardClass: 'Markets-main-card primary',
    },
    {
      title: 'Active Markets',
      value: stats?.active,
      subtitle: 'This Month',
      change: 0.36,
      icon: 'ri-check-line',
      color: 'secondary',
      cardClass: 'Markets-main-card secondary',
    },
    {
      title: 'Inactive Markets',
      value: stats?.inactive,
      subtitle: 'This Month',
      change: -1.28,
      icon: 'ri-close-line',
      color: 'warning',
      cardClass: 'Markets-main-card warning',
    },
  ];

  return (
    <Page currentPage="Markets">
      <StatsRowComponent stats={statsRow} />
      <DataTableRpc
        title="Markets Summary"
        searchColumn="market_name"
        functionName="get_market_summary"
        columns={[
          {
            header: 'Name',
            field: 'market_name',
            render: row => (
              <Link className="text-primary hover:text-primary dark:text-primary" to={`/markets/${row.market_id}`}>
                {row.market_name}
              </Link>
            ),
          },
          {header: 'Total Distributors', field: 'total_distributors'},
          {header: 'Total Brands', field: 'total_brands'},
          {header: 'Active Positions', field: 'active_positions'},
        ]}
        onAdd={handleAdd}
        actions={[
          {
            icon: 'ri-pencil-line',
            color: 'info',
            handler: row => handleEdit(row.market_id),
            permission: () => isAdmin,
          },
          {
            icon: 'ri-delete-bin-line',
            color: 'danger',
            handler: row => handleDeleteEvent(row.market_id),
            permission: () => isAdmin,
          },
        ]}
      />
      <ModalFormComponent
        isVisible={isVisible}
        fields={isEditing ? editFields : createFields}
        formData={formDataRef.current}
        onSubmit={(data: marketT) => {
          setSubmissionError(null); // Clear previous error before submitting
          if (isEditing) {
            handleUpdate(data);
          } else {
            handleCreate(data);
          }
        }}
        onClose={() => setIsVisible(false)}
        error={submissionError} // Pass the error to the modal
        isSubmitting={isSubmitting} // Pass the loading state to the modal
        title={isEditing ? 'Edit Speculator' : 'Create New Market'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this speculator? This action cannot be undone."
        isSubmitting={isSubmitting}
        error={deleteError}
      />
    </Page>
  );
}

export default AdminMarketsPage;
